import React, { lazy } from "react";

import Landing from "../pages/Landing";
import ScanPage from "../pages/Scan";
import ContractPage from "../pages/Contract";
import AtlasPage from "../pages/Atlas";
import MethodologyPage from "../pages/Methodology";
import MethodologyVersionPage from "../pages/MethodologyVersion";
import ReportPage from "../pages/Report";
import Audit from "../pages/Audit";
import AuditPreview from "../pages/AuditPreview";
import PricingAudits from "../pages/PricingAudits";
import AuditTalkToSales from "../pages/AuditTalkToSales";
import AuditCheckoutSuccess from "../pages/AuditCheckoutSuccess";
import DemoCheckoutSuccess from "../pages/DemoCheckoutSuccess";
import DemoShareRedeem from "../pages/DemoShareRedeem";
import Unsubscribe from "../pages/Unsubscribe";
import VerifyWebhook from "../pages/VerifyWebhook";
import SdkSamples from "../pages/SdkSamples";
import WhatsNew from "../pages/WhatsNew";
import SignIn from "../pages/SignIn";
import { ForgotPassword, ResetPassword } from "../pages/PasswordReset";
import Terms from "../pages/legal/Terms";
import Privacy from "../pages/legal/Privacy";
import CookiesPolicy from "../pages/legal/Cookies";
import Security from "../pages/legal/Security";

// Marketing-funnel pages: lazy + prefetched. webpack injects
// <link rel="prefetch"> for these chunks into the landing HTML, so
// the browser fetches them during idle after the homepage renders.
// Result: tiny first-paint AND instant navigation to the highest-
// conversion next-steps (FreeScan, Pricing).
const FreeScan = lazy(() =>
  import(/* webpackChunkName: "freescan", webpackPrefetch: true */ "../pages/FreeScan")
);
const ScoreMyContractRedirect = lazy(() =>
  import(/* webpackChunkName: "score-my-contract" */ "../pages/ScoreMyContractRedirect")
);
const ScanResult = lazy(() =>
  import(/* webpackChunkName: "scan-result" */ "../pages/ScanResult")
);
const Pricing = lazy(() =>
  import(/* webpackChunkName: "pricing", webpackPrefetch: true */ "../pages/Pricing")
);
// Verify is shared externally on social — prefetch so incoming links
// from LinkedIn/Twitter previews feel instant.
const VerifyPage = lazy(() =>
  import(/* webpackChunkName: "verify", webpackPrefetch: true */ "../pages/Verify")
);

// Public docs platform (Phase 2b). Lazy + prefetched on landing so the IA
// front door + article reader are instant after the homepage paints.
const DocsLanding = lazy(() =>
  import(/* webpackChunkName: "docs-landing", webpackPrefetch: true */ "../pages/docs/DocsLanding")
);
const DocsArticle = lazy(() =>
  import(/* webpackChunkName: "docs-article", webpackPrefetch: true */ "../pages/docs/DocsArticle")
);

// Phase 8.1 — Help Center hub.
const HelpCenter = lazy(() =>
  import(/* webpackChunkName: "help-center", webpackPrefetch: true */ "../pages/HelpCenter")
);
const HelpCategory = lazy(() =>
  import(/* webpackChunkName: "help-category" */ "../pages/HelpCategory")
);
// Phase 8.4 — Public Community Pulse / Trends.
const PublicTrends = lazy(() =>
  import(/* webpackChunkName: "public-trends" */ "../pages/PublicTrends")
);
// Phase 4 — Public Trust & Compliance page.
const Trust = lazy(() =>
  import(/* webpackChunkName: "public-trust", webpackPrefetch: true */ "../pages/Trust")
);
const TrustPolicyView = lazy(() =>
  import(/* webpackChunkName: "public-trust-policy" */ "../pages/Trust").then((m) => ({ default: m.TrustPolicyView }))
);
// Phase 12.6 — Standalone Threat Model page.
const ThreatModel = lazy(() =>
  import(/* webpackChunkName: "public-threat-model" */ "../pages/ThreatModel")
);
// Phase 12.6 — Sister Brands hub.
const SisterBrands = lazy(() =>
  import(/* webpackChunkName: "public-sister-brands" */ "../pages/SisterBrands")
);
// Phase 15.1 / M5 — Q-Day Tax Clock + embeddable widget.
const QDayClock = lazy(() =>
  import(/* webpackChunkName: "qday-clock", webpackPrefetch: true */ "../pages/QDayClock")
);
const QDayCounterWidget = lazy(() =>
  import(/* webpackChunkName: "qday-widget" */ "../pages/QDayCounterWidget")
);
// Phase 12.7c — Public Release log + Atom/RSS feeds.
const Releases = lazy(() =>
  import(/* webpackChunkName: "public-releases" */ "../pages/Releases")
);
// Phase 12.5 — Trust Center audit-updates unsubscribe landing.
const TrustAuditUpdatesUnsubscribe = lazy(() =>
  import(/* webpackChunkName: "public-trust-unsub" */ "../pages/TrustAuditUpdatesUnsubscribe")
);
// Phase 5 Sprint 4+5 — Public Threat Radar (marketing weapon).
const ThreatRadar = lazy(() =>
  import(/* webpackChunkName: "threat-radar", webpackPrefetch: true */ "../pages/ThreatRadar")
);
const ThreatRadarEmbed = lazy(() =>
  import(/* webpackChunkName: "threat-radar-embed" */ "../pages/ThreatRadarEmbed")
);
const ThreatRadarStats = lazy(() =>
  import(/* webpackChunkName: "threat-radar-stats" */ "../pages/ThreatRadarStats")
);
// Iter203 — Customer-curated public radars (pinnable from security.txt).
const CustomerThreatRadarPublic = lazy(() =>
  import(/* webpackChunkName: "customer-threat-radar" */ "../pages/CustomerThreatRadarPublic")
);

// Phase 5 Sprint 6 — Public service status / uptime.
const StatusPage = lazy(() =>
  import(/* webpackChunkName: "status" */ "../pages/Status")
);
// Country-shortcut redirect — /r/<iso2> → /threat-radar with pre-pan params
const RegionRedirect = lazy(() =>
  import(/* webpackChunkName: "region-redirect" */ "../pages/RegionRedirect")
);

// Phase 5.5 — QorTrace Labs sub-brand site (also served at labs.qortrace.com).
const Labs = lazy(() =>
  import(/* webpackChunkName: "labs", webpackPrefetch: true */ "../pages/Labs")
);
// Iter248 — Public Wall of Shipped (curated Track B social archive).
const WallOfShipped = lazy(() =>
  import(/* webpackChunkName: "wall-of-shipped" */ "../pages/WallOfShipped")
);
const LabsService = lazy(() =>
  import(/* webpackChunkName: "labs-service" */ "../pages/LabsService")
);
const LabsManifesto = lazy(() =>
  import(/* webpackChunkName: "labs-manifesto" */ "../pages/LabsManifesto")
);
const LabsMethodology = lazy(() =>
  import(/* webpackChunkName: "labs-methodology" */ "../pages/LabsMethodology")
);
const LabsEngagements = lazy(() =>
  import(/* webpackChunkName: "labs-engagements" */ "../pages/LabsEngagements")
);
const LabsInsight = lazy(() =>
  import(/* webpackChunkName: "labs-insight" */ "../pages/LabsInsight")
);
const LabsVerify = lazy(() =>
  import(/* webpackChunkName: "labs-verify" */ "../pages/LabsVerify")
);
const LabsPlaybook = lazy(() =>
  import(/* webpackChunkName: "labs-playbook" */ "../pages/LabsPlaybook")
);
// Iter268 — MPC Vendor Matrix (productised subscription).
const MpcVendorMatrix = lazy(() =>
  import(/* webpackChunkName: "mpc-vendor-matrix" */ "../pages/MpcVendorMatrix")
);
const ContractSigningPortal = lazy(() =>
  import(/* webpackChunkName: "contract-signing" */
         "../pages/ContractSigningPortal")
);

// Iter270 — Pricing IA rebuild: hub + enterprise + monitoring pages.
const PricingHub = lazy(() =>
  import(/* webpackChunkName: "pricing-hub" */ "../pages/PricingHub")
);
const PricingEnterprise = lazy(() =>
  import(/* webpackChunkName: "pricing-enterprise" */ "../pages/PricingEnterprise")
);
const PricingMonitoring = lazy(() =>
  import(/* webpackChunkName: "pricing-monitoring" */ "../pages/PricingMonitoring")
);

// Iter279 — Track A PR amplification layer
const ExposedWallets = lazy(() =>
  import(/* webpackChunkName: "exposed-wallets" */ "../pages/ExposedWallets")
);
const BulkScan = lazy(() =>
  import(/* webpackChunkName: "bulk-scan" */ "../pages/BulkScan")
);

// Phase 10 iter 4 — counsel-facing matter view (token-gated)
const LegalCounselView = lazy(() =>
  import(/* webpackChunkName: "legal-counsel" */ "../pages/LegalCounselView")
);

// M3a — QorBOM partner-application landing (sister-brand B2B funnel).
// Temporary home inside QorTrace frontend until M3b ships the dedicated
// qorbom.com site. Has its own Light + Cobalt visual identity, no
// QorTrace Header/Footer.
const BecomeAQorbomPartner = lazy(() =>
  import(/* webpackChunkName: "qorbom-partners" */ "../pages/BecomeAQorbomPartner")
);

// M3a Step 2 — QorBOM partner API developer reference + OpenAPI 3.1
const QorbomApiDocs = lazy(() =>
  import(/* webpackChunkName: "qorbom-api-docs" */ "../pages/QorbomApiDocs")
);
// Phase 14 Day-2 — Scalar-rendered API reference (auto from OpenAPI spec)
const QorbomApiReference = lazy(() =>
  import(/* webpackChunkName: "qorbom-api-reference" */ "../pages/QorbomApiReference")
);
// Phase 14 Day-6 — Partner self-service portal
const QorbomPartnerPortal = lazy(() =>
  import(/* webpackChunkName: "qorbom-portal" */ "../pages/QorbomPartnerPortal")
);
// QorBOM trust & compliance page — for procurement teams
const QorbomTrust = lazy(() =>
  import(/* webpackChunkName: "qorbom-trust" */ "../pages/QorbomTrust")
);
// Phase 14.3 — QorBOM marketing landing for qorbom.com hostname.
const QorbomHomepage = lazy(() =>
  import(/* webpackChunkName: "qorbom-home" */ "../pages/QorbomHomepage")
);
// Phase 14 Day-7 — tenant-slug white-label landing for {slug}.qorbom.com
const QorbomTenantLanding = lazy(() =>
  import(/* webpackChunkName: "qorbom-tenant-landing" */ "../pages/QorbomTenantLanding")
);


/**
 * BrandAwareRoot — `/` is rendered differently per hostname:
 *
 *   {slug}.qorbom.com  → QorbomTenantLanding (white-label partner page)
 *   qorbom.com (apex)  → QorbomHomepage      (B2B marketing site)
 *   anywhere else      → Landing             (QorTrace primary brand)
 *
 * SSR-safe (returns Landing during prerender, swap happens client-
 * side as soon as `window` is available).
 */
function BrandAwareRoot() {
  if (typeof window === "undefined") return <Landing />;
  const host = (window.location.hostname || "").toLowerCase();
  // Tenant subdomain pattern: a single label in front of qorbom.com,
  // BUT skip reserved operational labels (www, docs, status, api, etc.)
  // — those should fall through to the apex marketing homepage.
  // Mirrors backend _RESERVED_SUBDOMAINS in cbom_routes.py.
  const tenantMatch = host.match(/^([a-z0-9][a-z0-9-]{1,30}[a-z0-9]?)\.qorbom\.com$/i);
  if (tenantMatch && !QORBOM_RESERVED_SUBDOMAINS.has(tenantMatch[1])) {
    return <QorbomTenantLanding />;
  }
  if (/(?:^|\.)qorbom\.com$/i.test(host)) {
    return <QorbomHomepage />;
  }
  return <Landing />;
}

// Subdomains that should serve the QorBOM apex marketing page instead of
// being interpreted as a tenant slug. Keep in sync with
// backend/cbom_routes.py::_RESERVED_SUBDOMAINS.
const QORBOM_RESERVED_SUBDOMAINS = new Set([
  "www", "api", "app", "mail", "admin", "auth", "login", "signup",
  "billing", "docs", "status", "support", "help", "blog", "press",
  "trust", "security", "static", "assets", "cdn", "img", "images",
  "media", "files", "download", "downloads", "ws", "websocket",
  "smtp", "imap", "pop", "ftp", "ssh", "vpn", "ns1", "ns2",
  "monitor", "metrics", "logs", "stats", "console", "dashboard",
  "portal", "go", "links", "track", "tracking", "email",
  "noreply", "no-reply", "hello", "team", "partners", "partner",
  "internal", "qortrace", "qorbom", "qor", "test", "staging",
  "preview", "demo", "sandbox", "dev", "development", "prod",
  "production", "qa", "uat", "developers",
]);


const publicRoutes = [
  { path: "/", element: <BrandAwareRoot /> },
  // Direct test/preview route for QorbomHomepage so we can hit it
  // from the preview URL without spoofing the hostname.
  { path: "/qorbom-home", element: <QorbomHomepage /> },
  // Preview-only test route for tenant landings. In production
  // these render at `{slug}.qorbom.com/` via BrandAwareRoot.
  { path: "/qorbom-tenant-preview", element: <QorbomTenantLanding /> },
  { path: "/account/contracts/:contractId", element: <ContractSigningPortal /> },
  { path: "/scan", element: <ScanPage /> },
  { path: "/scan/bulk", element: <BulkScan /> },
  { path: "/threat-radar/exposed-wallets", element: <ExposedWallets /> },
  { path: "/contract", element: <ContractPage /> },
  { path: "/atlas", element: <AtlasPage /> },
  { path: "/methodology", element: <MethodologyPage /> },
  { path: "/methodology/:slug", element: <MethodologyVersionPage /> },
  { path: "/verify", element: <VerifyPage /> },
  { path: "/verify/:auditId", element: <VerifyPage /> },
  // Iter270 — Pricing IA rebuild.
  // /pricing → decision page (the new PricingHub).
  // /pricing/scanner → the original Pricing component (scanner SaaS tiers).
  // /pricing/enterprise → PQC Audit Suite primary home.
  // /pricing/monitoring → Atlas + MPC Vendor Matrix.
  // /pricing/audits is preserved for backwards-compat — it now redirects
  // its #pqc-suite traffic through to /pricing/enterprise via the Suite
  // configurator embedded at both places.
  { path: "/pricing", element: <PricingHub /> },
  { path: "/pricing/scanner", element: <Pricing /> },
  { path: "/pricing/enterprise", element: <PricingEnterprise /> },
  { path: "/pricing/monitoring", element: <PricingMonitoring /> },
  { path: "/free-scan", element: <FreeScan /> },
  { path: "/score-my-contract", element: <ScoreMyContractRedirect /> },
  { path: "/scan/result/:scanId", element: <ScanResult /> },
  { path: "/audit", element: <Audit /> },
  { path: "/audit/preview", element: <AuditPreview /> },
  { path: "/audit/talk-to-sales", element: <AuditTalkToSales /> },
  { path: "/audit/billing/success", element: <AuditCheckoutSuccess /> },
  { path: "/demo/checkout-success", element: <DemoCheckoutSuccess /> },
  { path: "/d/:token", element: <DemoShareRedeem /> },
  { path: "/legal/counsel/:token", element: <LegalCounselView /> },
  { path: "/pricing/audits", element: <PricingAudits /> },
  { path: "/signin", element: <SignIn /> },
  { path: "/signup", element: <SignIn /> },
  { path: "/account/forgot-password", element: <ForgotPassword /> },
  { path: "/account/reset-password",  element: <ResetPassword /> },
  { path: "/unsubscribe", element: <Unsubscribe /> },
  { path: "/tools/verify-webhook", element: <VerifyWebhook /> },
  // Phase 2b — public docs surface. /docs is the IA landing, /docs/* renders
  // any published wiki page (including nested slugs like internal/team-runbook).
  { path: "/docs", element: <DocsLanding /> },
  { path: "/docs/sdks", element: <SdkSamples /> },
  { path: "/docs/*", element: <DocsArticle /> },
  // Phase 8.1 — Help Center hub.
  { path: "/help", element: <HelpCenter /> },
  { path: "/help/:category", element: <HelpCategory /> },
  // Phase 8.4 — Public Community Pulse.
  { path: "/trends", element: <PublicTrends /> },
  // Phase 4 — Public Trust & Compliance Portal.
  { path: "/trust", element: <Trust /> },
  { path: "/trust/policies/:slug", element: <TrustPolicyView /> },
  { path: "/trust/audit-updates/unsubscribe",
    element: <TrustAuditUpdatesUnsubscribe /> },
  // Phase 12.6 — Standalone Threat Model page (auditor-facing).
  { path: "/threat-model", element: <ThreatModel /> },
  { path: "/sister-brands", element: <SisterBrands /> },
  // Phase 15.1 / M5 — Q-Day Tax Clock + embeddable widget.
  { path: "/q-day", element: <QDayClock /> },
  { path: "/widget/q-day-counter", element: <QDayCounterWidget /> },
  // Phase 12.7c — Public Release log.
  { path: "/releases", element: <Releases /> },
  { path: "/status", element: <StatusPage /> },
  { path: "/r/:code", element: <RegionRedirect /> },
  // Phase 5.5 — QorTrace Labs (sub-brand). Lives at /labs and labs.qortrace.com.
  { path: "/labs", element: <Labs /> },
  { path: "/labs/wall-of-shipped", element: <WallOfShipped /> },
  { path: "/labs/manifesto", element: <LabsManifesto /> },
  { path: "/labs/methodology", element: <LabsMethodology /> },
  { path: "/labs/engagements", element: <LabsEngagements /> },
  { path: "/labs/insights/:slug", element: <LabsInsight /> },
  { path: "/labs/services/:slug", element: <LabsService /> },
  { path: "/labs/mpc-vendor-matrix", element: <MpcVendorMatrix /> },
  { path: "/labs/verify/:certId", element: <LabsVerify /> },
  { path: "/labs/playbook/:token", element: <LabsPlaybook /> },
  // Phase 5 Sprint 4+5 — Public Threat Radar.
  { path: "/threat-radar", element: <ThreatRadar /> },
  { path: "/threat-radar/embed", element: <ThreatRadarEmbed /> },
  { path: "/threat-radar/stats", element: <ThreatRadarStats /> },
  // Iter203 — Customer-curated public PQC threat radars.
  { path: "/customer/:slug/threat-radar", element: <CustomerThreatRadarPublic /> },
  { path: "/whats-new", element: <WhatsNew /> },
  { path: "/report/:id", element: <ReportPage /> },
  { path: "/legal/terms", element: <Terms /> },
  { path: "/legal/privacy", element: <Privacy /> },
  { path: "/legal/cookies", element: <CookiesPolicy /> },
  { path: "/legal/security", element: <Security /> },
  // M3a — QorBOM sister-brand partner-application landing.
  //   • On qortrace.com:  served under `/qorbom/*` (legacy paths).
  //   • On qorbom.com:    served at the bare path (the CF Worker
  //     in /app/cloudflare/qorbom-router.js proxies qorbom.com/*
  //     to qortrace.com/*; the React app detects
  //     `window.location.hostname === 'qorbom.com'` and mounts
  //     these routes at root).
  { path: "/qorbom/become-a-partner", element: <BecomeAQorbomPartner /> },
  { path: "/qorbom/api-docs",         element: <QorbomApiDocs /> },
  { path: "/qorbom/api-reference",    element: <QorbomApiReference /> },
  { path: "/qorbom/portal",           element: <QorbomPartnerPortal /> },
  { path: "/qorbom/trust",            element: <QorbomTrust /> },
  // Phase 14.2 — qorbom.com hostname root paths. These render the
  // same Qorbom* components without the `/qorbom` prefix so the
  // URL bar reads `qorbom.com/become-a-partner` etc.
  { path: "/become-a-partner",        element: <BecomeAQorbomPartner /> },
  { path: "/developers",              element: <QorbomApiDocs /> },
  { path: "/api-reference",           element: <QorbomApiReference /> },
  { path: "/portal",                  element: <QorbomPartnerPortal /> },
];

export default publicRoutes;
